import { StyleSheet, css } from 'aphrodite'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import BlurbSection from '../components/BlurbSection'
import LandingSection from '../components/LandingSection'
import LandingFooter from '../components/navigation/LandingFooter'
import LandingHeader from '../components/navigation/LandingHeader'

export default function ThirdPartyEngagementsPage() {
  const navigate = useNavigate()

  const handleJoinCommunityClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }

  return (
    <div className={css(styles.container)}>
      <LandingHeader />
      <LandingSection
        title="COLLABORATIVE ECOSYSTEM PARTNERS"
        description="The Ecosystem fosters continuous contributions from independent third parties who share our vision of transparency, trust, and collaboration. Together, we're building a comprehensive platform that serves every stakeholder in the Dump Truck Industry."
        imageSrc="/thirdparty.jpg"
        imageAlt="Third-Party Engagements"
        buttonText="Join the Ecosystem"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
        showCallToAction={false}
        subtitle="Third-Party Engagements - Complementors"
      />

      {/* Collaborative Governance Section */}
      <section className={css(styles.digitalPlatformSection)}>
        <div className="flex align-center items-center gap-16 flex-col md:flex-row">
          <div className={css(styles.digitalPlatformContent)}>
            <h2 className={css(styles.digitalPlatformTitle)}>
              FOUNDED ON COLLABORATIVE GOVERNANCE
            </h2>
            <p className={css(styles.digitalPlatformDescription)}>
              The Ecosystem is founded on the principles of collaborative
              governance. It fosters the continuous contributions of independent
              third parties.
            </p>
          </div>
          <div className={css(styles.digitalPlatformImageContainer)}>
            <img
              src="/collaboration.webp"
              alt="Collaborative Governance"
              className={css(styles.digitalPlatformImage)}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Two Purposes Section */}
      <BlurbSection>
        Third-party engagements share two important purposes with the Community:{' '}
        <br />
        <br />
        <strong>First,</strong> third-party engagements enhance the Ecosystem's
        functionalities.
        <br />
        <br />
        <strong>Second,</strong> third-party engagements participate in the
        Ecosystems in order to contribute new and additional benefit and value
        to the Participants.
      </BlurbSection>

      {/* Complementors Section */}
      <section className={css(styles.complementorsSection)}>
        <div className={css(styles.complementorsContent)}>
          <h2 className={css(styles.complementorsTitle)}>
            COMPLEMENTORS: ADDING VALUE TO EVERY STAKEHOLDER
          </h2>
          <p className={css(styles.complementorsDescription)}>
            The Ecosystem provides resources to stimulate complementor
            engagements. Complementors are third parties who add important value
            directly benefiting each stakeholder - Contractor, Broker, Sub
            Hauler, and Driver - in the Ecosystem.
          </p>

          <div className={css(styles.examplesContainer)}>
            <h3 className={css(styles.examplesTitle)}>Examples include:</h3>
            <ul className={css(styles.examplesList)}>
              <li className={css(styles.exampleItem)}>
                <span className={css(styles.bulletPoint)}>•</span>
                <span>Dump Truck focused insurance companies</span>
              </li>
              <li className={css(styles.exampleItem)}>
                <span className={css(styles.bulletPoint)}>•</span>
                <span>Factoring companies - instant pay</span>
              </li>
              <li className={css(styles.exampleItem)}>
                <span className={css(styles.bulletPoint)}>•</span>
                <span>Dump Truck dedicated maintenance and repair shops</span>
              </li>
              <li className={css(styles.exampleItem)}>
                <span className={css(styles.bulletPoint)}>•</span>
                <span>
                  Health care support and providers specializing in the care of
                  Dump Truck Drivers (i.e., lower back pain for Drivers due to
                  joint-compression, and knee-issues which can become chronic
                  injuries commonly experienced)
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <LandingSection
        title="READY TO JOIN THE COMMUNITY?"
        description="Join our network of third-party partners who are enhancing the Dump Truck Ecosystem. Together, we can create more value for every stakeholder in our community."
        imageSrc="/ecosystem.webp"
        imageAlt="Partnership Opportunities"
        buttonText="Join the Ecosystem"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
      />

      <LandingFooter />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  digitalPlatformSection: {
    gap: '32px',
    alignItems: 'center',
    padding: '40px 1rem',
    backgroundColor: '#ffffff',
    '@media (min-width: 768px)': {
      gap: '40px',
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '48px',
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  digitalPlatformContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  digitalPlatformTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  digitalPlatformDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    fontWeight: 400,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  digitalPlatformImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: -1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  digitalPlatformImage: {
    height: 'auto',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '16px',
    '@media (min-width: 768px)': {
      borderRadius: '20px',
      maxWidth: '90%',
    },
    '@media (min-width: 1024px)': {
      borderRadius: '24px',
      maxWidth: '100%',
    },
  },
  complementorsSection: {
    padding: '40px 1rem',
    backgroundColor: '#ffffff',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
      textAlign: 'left',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  complementorsContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  complementorsTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    textAlign: 'center',
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  complementorsDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    fontWeight: 400,
    textAlign: 'center',
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
      textAlign: 'left',
    },
  },
  examplesContainer: {
    backgroundColor: '#F9FAFB',
    padding: '32px',
    borderRadius: '16px',
    marginTop: '16px',
  },
  examplesTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F3D61',
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      fontSize: '20px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '22px',
    },
  },
  examplesList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  exampleItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
  bulletPoint: {
    color: '#6DC3FF',
    fontSize: '20px',
    fontWeight: 'bold',
    flexShrink: 0,
    marginTop: '-2px',
  },
})
